import { bien, fechaValida, mal, type Resultado } from './validar';
import { limpiar } from './texto';
import { problemaConNombre, claveDePareja } from './parejas';

export interface OpcionesTorneo {
  modo_horarios: 'sin_horarios' | 'manual' | 'asistido';
  disponibilidad_activa: boolean;
  descanso_activo: boolean;
  control_canchas_activo: boolean;
  premios_activos: boolean;
  imagenes_activas: boolean;
  mensajes_activos: boolean;
  inscripcion_publica: boolean;
  descanso_minutos: number;
  duracion_minutos: number;
}
export type ConfiguracionTorneo = OpcionesTorneo & { revisiones: Record<string, number> };
export const OPCIONES_INICIALES: OpcionesTorneo = {
  modo_horarios: 'asistido', disponibilidad_activa: true, descanso_activo: false,
  control_canchas_activo: true, premios_activos: true, imagenes_activas: true, mensajes_activos: true,
  inscripcion_publica: false, descanso_minutos: 0, duracion_minutos: 60,
};
export function capacidades(o: OpcionesTorneo) {
  const horarios = o.modo_horarios !== 'sin_horarios';
  return { horarios, asistente: o.modo_horarios === 'asistido',
    disponibilidad: horarios && o.disponibilidad_activa,
    descansoMinutos: horarios && o.descanso_activo ? o.descanso_minutos : 0,
    control: o.control_canchas_activo, premios: o.premios_activos,
    imagenes: o.imagenes_activas, mensajes: o.mensajes_activos,
    inscripcion: o.inscripcion_publica };
}
export interface Restriccion { dia: string; desde: string; hasta: string }
export interface Disponibilidad { pareja_id: number; restricciones: Restriccion[] }
export interface Solicitud {
  id: number; torneo_id: number; categoria_id: number; jugador1: string; jugador2: string;
  contacto: string; clave: string; estado: 'pendiente' | 'aprobada' | 'rechazada'; creado_en: number;
}
export const LUGARES_IMAGEN = ['logo', 'portada', 'imagen1', 'imagen2', 'imagen3'] as const;
export type LugarImagen = typeof LUGARES_IMAGEN[number];
export interface ImagenTorneo { lugar: LugarImagen; version: string }
export const MAXIMO_IMAGEN = 250_000;
const objeto = (v: unknown): Record<string, unknown> => typeof v === 'object' && v !== null ? v as Record<string, unknown> : {};

export function validarOpciones(v: unknown, anteriores: OpcionesTorneo = OPCIONES_INICIALES): Resultado<OpcionesTorneo> {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return mal('Las opciones no son válidas.');
  const enviados = objeto(v);
  if (Object.keys(enviados).some(k => !Object.hasOwn(OPCIONES_INICIALES, k))) return mal('Hay una opción desconocida.');
  const c = { ...anteriores, ...enviados };
  if (!['sin_horarios', 'manual', 'asistido'].includes(String(c.modo_horarios))) return mal('Elegí un modo de horarios válido.');
  for (const k of ['disponibilidad_activa', 'descanso_activo', 'control_canchas_activo', 'premios_activos', 'imagenes_activas', 'mensajes_activos'] as const) {
    if (typeof c[k] !== 'boolean') return mal('Las herramientas se activan o desactivan.');
  }
  if (typeof c.inscripcion_publica !== 'boolean') return mal('Elegí si los jugadores pueden inscribirse.');
  if (typeof c.descanso_minutos !== 'number' || !Number.isInteger(c.descanso_minutos) || c.descanso_minutos < 0 || c.descanso_minutos > 240) return mal('El descanso va de 0 a 240 minutos; 0 permite jugar seguido.');
  if (typeof c.duracion_minutos !== 'number' || !Number.isInteger(c.duracion_minutos) || c.duracion_minutos < 15 || c.duracion_minutos > 180) return mal('La duración estimada de cada partido va de 15 a 180 minutos.');
  if (c.descanso_activo && c.descanso_minutos < 1) return mal('El descanso activo requiere de 1 a 240 minutos.');
  return bien({ modo_horarios: c.modo_horarios as OpcionesTorneo['modo_horarios'],
    inscripcion_publica: c.inscripcion_publica, descanso_minutos: c.descanso_minutos, duracion_minutos: c.duracion_minutos,
    disponibilidad_activa: c.disponibilidad_activa, descanso_activo: c.descanso_activo,
    control_canchas_activo: c.control_canchas_activo, premios_activos: c.premios_activos,
    imagenes_activas: c.imagenes_activas, mensajes_activos: c.mensajes_activos });
}

export function validarRestricciones(v: unknown, fechas: { fecha_desde: string; fecha_hasta: string }): Resultado<Restriccion[]> {
  if (!Array.isArray(v) || v.length > 30) return mal('Podés cargar hasta 30 franjas por pareja.');
  const franjas: Restriccion[] = [];
  const hora = /^([01]\d|2[0-3]):[0-5]\d$/;
  for (const x of v) {
    const c = objeto(x);
    if (!fechaValida(c.dia) || c.dia < fechas.fecha_desde || c.dia > fechas.fecha_hasta) return mal('La disponibilidad debe estar dentro de las fechas del torneo.');
    if (typeof c.desde !== 'string' || typeof c.hasta !== 'string' || !hora.test(c.desde) || !hora.test(c.hasta) || c.desde >= c.hasta) return mal('Cada franja necesita una hora de inicio anterior a la de fin. Para cruzar medianoche, cargá una franja por día.');
    franjas.push({ dia: c.dia, desde: c.desde, hasta: c.hasta });
  }
  return bien(franjas);
}

export function validarSolicitud(v: unknown) {
  const c = objeto(v);
  const jugador1 = typeof c.jugador1 === 'string' ? limpiar(c.jugador1) : '';
  const jugador2 = typeof c.jugador2 === 'string' ? limpiar(c.jugador2) : '';
  const problema = problemaConNombre(jugador1) ?? problemaConNombre(jugador2);
  if (problema) return mal(problema);
  const contacto = typeof c.contacto === 'string' ? limpiar(c.contacto) : '';
  if (contacto.length < 6 || contacto.length > 80) return mal('Dejá un teléfono o email de contacto (6 a 80 caracteres).');
  if (typeof c.categoria_id !== 'number' || !Number.isInteger(c.categoria_id) || c.categoria_id < 1) return mal('Elegí una categoría.');
  return bien({ jugador1, jugador2, contacto, categoria_id: c.categoria_id, clave: claveDePareja(jugador1, jugador2) });
}

/** Solo imágenes raster con firma válida. Nunca SVG/HTML activo. */
export function validarImagen(v: unknown): Resultado<string> {
  if (typeof v !== 'string' || v.length > MAXIMO_IMAGEN) return mal('La imagen es demasiado grande. Elegí una más chica.');
  const m = v.match(/^data:image\/(png|jpeg|webp);base64,([A-Za-z0-9+/]+={0,2})$/);
  if (!m) return mal('Usá una imagen PNG, JPG o WebP.');
  let bytes: string;
  try { bytes = atob(m[2]!); } catch { return mal('La imagen no se entiende.'); }
  const firma = m[1] === 'png' ? bytes.startsWith('\x89PNG\r\n\x1a\n') : m[1] === 'jpeg' ? bytes.startsWith('\xff\xd8\xff') : bytes.startsWith('RIFF') && bytes.slice(8, 12) === 'WEBP';
  return firma ? bien(v) : mal('El archivo no es una imagen válida.');
}
